import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandHelping, Sparkles, Trash2, Save, Plus, CheckCircle2, X, ImageIcon, Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { mediaUrl } from "../../lib/mediaUrl";

// Section admin « Menus à gérer » — restaurateurs ayant activé la gestion assistée (paiement unique 195€).
// L'admin voit la liste des restos, leur dernière demande envoyée par le restaurateur,
// et peut CRUD leurs plats directement depuis ce panneau.
export default function AssistedMenusAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // row en cours d'édition

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/assisted-menus");
      setRows(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markDone = async (userId) => {
    if (!window.confirm("Marquer la demande comme traitée ? Le restaurateur recevra une notification.")) return;
    try {
      await api.post(`/admin/assisted-menus/${userId}/mark-done`);
      toast.success("Demande marquée comme traitée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F26419]" /></div>
  );

  return (
    <div data-testid="admin-assisted-menus" className="space-y-4">
      <div className="bg-white rounded-2xl border-2 border-[#F26419]/40 p-5">
        <div className="flex items-center gap-2">
          <HandHelping className="w-5 h-5 text-[#F26419]" />
          <h3 className="font-head font-black text-lg text-[#1A1A1A]">Menus à gérer</h3>
          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#F26419]/15 text-[#B85014] font-bold">{rows.length}</span>
        </div>
        <p className="mt-1 text-xs text-[#75807A]">Restaurateurs ayant activé la gestion assistée (paiement unique 195 €). Ajoutez, modifiez ou supprimez leurs plats directement.</p>
      </div>

      {rows.length === 0 && (
        <div className="rounded-2xl bg-white border border-dashed border-[#E2DFD8] p-8 text-center text-sm text-[#75807A]">
          Aucun restaurateur n'a activé la gestion assistée pour le moment.
        </div>
      )}

      {rows.map((r) => (
        <RowCard key={r.user_id} row={r} onEdit={() => setSelected(r)} onMarkDone={() => markDone(r.user_id)} />
      ))}

      {selected && <MenuEditor row={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}


function RowCard({ row, onEdit, onMarkDone }) {
  const a = row.assisted || {};
  const lr = a.last_request || null;
  const statusColor = a.enabled ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]";
  return (
    <div className="rounded-2xl bg-white border border-[#E2DFD8] p-4 hover:border-[#F26419]/40 transition-colors" data-testid={`assisted-row-${row.user_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-head font-bold text-[#1A1A1A]">{row.business_name || row.restaurant?.name || row.name || row.email}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${statusColor}`}>{a.status}</span>
            {lr?.status === "pending" && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-[#FEE2E2] text-[#B91C1C]">Demande en attente</span>}
          </div>
          <div className="text-xs text-[#75807A] mt-0.5 truncate">
            {row.email} · {row.restaurant?.address || "—"} {row.restaurant?.commune ? "· " + row.restaurant.commune : ""}
          </div>
          <div className="text-xs text-[#3B423E] mt-1">🍽 <b>{row.menu_count}</b> plat{row.menu_count !== 1 ? "s" : ""} en ligne</div>
          {lr && (
            <div className="mt-2 rounded-lg bg-[#FDF8F0] border border-[#F0EBE1] p-2">
              <div className="text-xs font-semibold text-[#1A1A1A]">📩 Dernière demande : {lr.dishes?.length || 0} plat(s)</div>
              {lr.notes && <div className="text-xs text-[#75807A] italic mt-0.5">« {lr.notes} »</div>}
              {lr.dishes?.length > 0 && (
                <ul className="mt-1 text-xs text-[#3B423E] space-y-0.5">
                  {lr.dishes.slice(0, 5).map((d, i) => (
                    <li key={i}>• {d.name}{d.price_eur ? ` — ${d.price_eur} €` : ""}{d.category ? ` (${d.category})` : ""}</li>
                  ))}
                  {lr.dishes.length > 5 && <li className="text-[#75807A]">… +{lr.dishes.length - 5} plats</li>}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button data-testid={`edit-menu-${row.user_id}`} onClick={onEdit}
            className="h-9 px-3 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-xs inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Gérer les plats
          </button>
          {lr?.status === "pending" && (
            <button data-testid={`mark-done-${row.user_id}`} onClick={onMarkDone}
              className="h-9 px-3 rounded-lg bg-white border-2 border-[#16a34a] text-[#16a34a] font-bold text-xs inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Marquer traitée
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function MenuEditor({ row, onClose }) {
  const rid = row.restaurant_id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ name: "", price_eur: "", description: "", category: "", photo_data_url: null });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assisted-menus/${rid}/menu`);
      setItems(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (rid) load(); }, [rid]);

  const readFile = (file, cb) => {
    if (!file) return cb(null);
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo trop lourde (8 Mo max)."); return; }
    const r = new FileReader();
    r.onload = () => cb(r.result);
    r.readAsDataURL(file);
  };

  const createItem = async () => {
    if (!newItem.name.trim() || newItem.name.trim().length < 2) return toast.error("Nom du plat requis.");
    if (newItem.price_eur === "" || isNaN(parseFloat(newItem.price_eur))) return toast.error("Prix invalide.");
    setSaving(true);
    try {
      await api.post(`/admin/assisted-menus/${rid}/menu`, {
        name: newItem.name.trim(),
        price_eur: parseFloat(newItem.price_eur),
        description: newItem.description.trim(),
        category: newItem.category.trim(),
        active: true,
        position: items.length,
        photo_data_url: newItem.photo_data_url || null,
      });
      toast.success("Plat ajouté");
      setNewItem({ name: "", price_eur: "", description: "", category: "", photo_data_url: null });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const patchItem = async (id, patch) => {
    try {
      await api.patch(`/admin/assisted-menus/${rid}/menu/${id}`, patch);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Supprimer ce plat ?")) return;
    try {
      await api.delete(`/admin/assisted-menus/${rid}/menu/${id}`);
      toast.success("Plat supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="menu-editor-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">Gestion assistée Nold Go</div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A] mt-0.5">{row.business_name || row.restaurant?.name}</h3>
            <div className="text-xs text-[#75807A] mt-0.5">{row.email} · {row.menu_count} plat(s) en ligne</div>
          </div>
          <button onClick={onClose} className="text-[#75807A] hover:text-[#1A1A1A] p-2 rounded-lg hover:bg-[#F0EBE1]"><X className="w-4 h-4" /></button>
        </div>

        {/* Demande initiale du restaurateur */}
        {row.assisted?.last_request?.dishes?.length > 0 && (
          <div className="mb-4 rounded-2xl bg-[#FFF3E6] border border-[#F26419]/30 p-3">
            <div className="text-sm font-bold text-[#1A1A1A] mb-1.5">📩 Demande à traiter — {row.assisted.last_request.dishes.length} plat(s) à intégrer</div>
            {row.assisted.last_request.notes && <div className="text-xs text-[#75807A] italic mb-2">« {row.assisted.last_request.notes} »</div>}
            <div className="space-y-1 text-xs">
              {row.assisted.last_request.dishes.map((d, i) => (
                <div key={i} className="flex items-start justify-between gap-2 bg-white rounded-lg px-2 py-1.5">
                  <div className="min-w-0">
                    <b>{d.name}</b>{d.price_eur ? ` — ${d.price_eur} €` : ""} <span className="text-[#75807A]">{d.category && ` · ${d.category}`}</span>
                    {d.description && <div className="text-[#75807A] mt-0.5">{d.description}</div>}
                  </div>
                  <button data-testid={`copy-to-form-${i}`} onClick={() => setNewItem({
                    name: d.name || "", price_eur: d.price_eur != null ? String(d.price_eur) : "",
                    description: d.description || "", category: d.category || "", photo_data_url: null
                  })} className="shrink-0 h-7 px-2 rounded bg-[#F26419] text-white text-[10px] font-bold hover:bg-[#D94D0E]">
                    Copier ↓
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Formulaire nouveau plat */}
        <div className="rounded-2xl bg-[#FDF8F0] border-2 border-dashed border-[#F26419]/40 p-3 mb-4">
          <div className="text-sm font-bold text-[#1A1A1A] mb-2 flex items-center gap-1.5"><Plus className="w-4 h-4 text-[#F26419]" /> Ajouter un plat</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input data-testid="new-dish-name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              placeholder="Nom*" className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-sm sm:col-span-2" />
            <input data-testid="new-dish-price" type="number" step="0.01" min="0" value={newItem.price_eur} onChange={(e) => setNewItem({ ...newItem, price_eur: e.target.value })}
              placeholder="Prix €*" className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-sm" />
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              placeholder="Catégorie" className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-sm" />
            <label className="h-10 rounded-lg border border-dashed border-[#E2DFD8] px-3 text-sm text-[#75807A] hover:border-[#F26419] cursor-pointer inline-flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> {newItem.photo_data_url ? "Photo ✓" : "Ajouter photo"}
              <input type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0], (v) => setNewItem({ ...newItem, photo_data_url: v }))} className="hidden" />
            </label>
          </div>
          <textarea value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            placeholder="Description" className="mt-2 w-full min-h-[45px] rounded-lg border border-[#E2DFD8] p-2 text-sm" />
          <button data-testid="add-dish-btn" onClick={createItem} disabled={saving}
            className="mt-2 h-10 px-4 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            {saving ? "Ajout…" : <><Plus className="w-4 h-4" /> Créer le plat</>}
          </button>
        </div>

        {/* Liste des plats existants — édition inline */}
        <div className="text-sm font-bold text-[#1A1A1A] mb-2">Plats en ligne ({items.length})</div>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#F26419]" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-lg bg-[#FDF8F0] p-4 text-center text-xs text-[#75807A]">Aucun plat pour ce restaurant.</div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <ExistingItemRow key={it.id} item={it} rid={rid} onPatch={patchItem} onDelete={deleteItem} readFile={readFile} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ExistingItemRow({ item, rid, onPatch, onDelete, readFile }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: item.name, price_eur: item.price_eur, description: item.description || "", category: item.category || "" });
  const [newPhoto, setNewPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const patch = {
      name: draft.name,
      price_eur: parseFloat(draft.price_eur),
      description: draft.description,
      category: draft.category,
    };
    if (newPhoto) patch.photo_data_url = newPhoto;
    await onPatch(item.id, patch);
    setEditing(false); setNewPhoto(null);
    setBusy(false);
  };

  if (!editing) {
    return (
      <div className="rounded-lg bg-white border border-[#E2DFD8] p-3 hover:border-[#F26419]/40 flex items-center gap-3" data-testid={`item-${item.id}`}>
        {item.photo_url ? (
          <img src={mediaUrl(item.photo_url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-[#F0EBE1] flex items-center justify-center shrink-0"><ImageIcon className="w-4 h-4 text-[#75807A]" /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-[#1A1A1A] truncate">{item.name}</div>
          <div className="text-xs text-[#75807A]">{item.price_eur} € · {item.category || "—"}</div>
        </div>
        <button data-testid={`edit-${item.id}`} onClick={() => setEditing(true)} className="h-8 px-3 rounded-lg bg-[#F26419]/15 text-[#B85014] font-bold text-xs">Éditer</button>
        <button data-testid={`delete-${item.id}`} onClick={() => onDelete(item.id)} className="h-8 w-8 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#DC2626] hover:text-[#DC2626] text-[#75807A] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-[#FDF8F0] border-2 border-[#F26419] p-3 space-y-2" data-testid={`edit-row-${item.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-9 rounded-lg border border-[#E2DFD8] px-3 text-sm sm:col-span-2" />
        <input type="number" step="0.01" min="0" value={draft.price_eur} onChange={(e) => setDraft({ ...draft, price_eur: e.target.value })} className="h-9 rounded-lg border border-[#E2DFD8] px-3 text-sm" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="Catégorie" className="h-9 rounded-lg border border-[#E2DFD8] px-3 text-sm" />
        <label className="h-9 rounded-lg border border-dashed border-[#E2DFD8] px-3 text-sm text-[#75807A] hover:border-[#F26419] cursor-pointer inline-flex items-center gap-2">
          <ImageIcon className="w-4 h-4" /> {newPhoto ? "Nouvelle photo ✓" : "Changer la photo"}
          <input type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0], setNewPhoto)} className="hidden" />
        </label>
      </div>
      <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full min-h-[40px] rounded-lg border border-[#E2DFD8] p-2 text-sm" />
      <div className="flex gap-2 justify-end">
        <button onClick={() => { setEditing(false); setNewPhoto(null); }} className="h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] text-[#75807A] font-bold text-xs">Annuler</button>
        <button data-testid={`save-${item.id}`} onClick={save} disabled={busy} className="h-9 px-3 rounded-lg bg-[#F26419] text-white font-bold text-xs inline-flex items-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> {busy ? "…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}
